import { NavigateBackLink } from '@exam-center/components';
import {
  EXAM_TYPE_LABEL,
  type ExamEventData,
  ExamEventRegistrationStatusLabel,
  type ExamEventRegistrationStatusLabelVariants,
  getExamEventScheduleTitle,
  MONTH_ORDER,
  REGISTRATION_TYPE_LABEL,
  useExamEventDetails,
} from '@exam-center/exam-event-management';
import { type RegistrationStatus } from '@exam-center/graphql';
import { useParams } from '@exam-center/router';
import { Flex, PageHeader, Text } from '@shared/components';

import { ProctoringDetailsNavigationTabs } from '../proctoring-details-navigation-tabs';

const EXAM_EVENT_REGISTRATION_STATUS_TO_EXAM_EVENT_LABEL_VARIANT: Record<
  RegistrationStatus,
  ExamEventRegistrationStatusLabelVariants
> = {
  OPEN_FOR_REGISTRATION: 'open-for-registration',
  REGISTRATION_CLOSED: 'registration-closed',
  SESSION_FULL: 'session-full',
  CANCELED: 'canceled',
  TEMPORARILY_CLOSED: 'temporarily-closed',
  SCHEDULED: 'scheduled',
};

function getExamEventHeaderDetails(examEvent: ExamEventData) {
  const { examType, registrationType, name } = examEvent;
  const examEventDate = new Date(examEvent.examStartedAt);
  const monthAbbr = MONTH_ORDER[examEvent.examStartedAt.getMonth()];
  const year = examEventDate.getFullYear();

  const regStatus = examEvent.registrationStatus;
  const regStatusVariant = EXAM_EVENT_REGISTRATION_STATUS_TO_EXAM_EVENT_LABEL_VARIANT[regStatus];
  const regType = REGISTRATION_TYPE_LABEL[registrationType];
  const examTypeLabel = EXAM_TYPE_LABEL[examType];

  return {
    name,
    examType: examTypeLabel,
    regType,
    month: monthAbbr,
    year,
    regStatus: regStatusVariant,
  };
}

export default function ProctoringDetailsPageHeader() {
  const { examCenterCode } = useParams('/:examCenterCode/proctoring/exam-events/:examEventId');
  const { examEvent } = useExamEventDetails();
  const { name, examType, regType, month, year, regStatus } = getExamEventHeaderDetails(examEvent);

  return (
    <PageHeader
      title={name}
      subtitleSection={
        <Flex
          sx={(theme) => ({
            gap: theme.spacing(4),
            color: theme.palette.text.secondary,
            flexWrap: 'wrap',
          })}
        >
          <Text variant='body2'>{getExamEventScheduleTitle(year, month)}</Text>
          <Text variant='body2'>Reg Type: {regType}</Text>
          <Text variant='body2'>Exam Type: {examType}</Text>
        </Flex>
      }
      tabs={<ProctoringDetailsNavigationTabs />}
      linkAdornment={
        <NavigateBackLink
          to='/:examCenterCode/proctoring'
          params={{
            examCenterCode: examCenterCode,
          }}
          label='Back'
        />
      }
      endAdornment={<ExamEventRegistrationStatusLabel variant={regStatus} />}
    />
  );
}
